// Story 11.8 — the admin user directory list read (Screen 20).
//
// Query key is ["admin-users", filters] (boundary #12): the filter object is part
// of the key, so two filter states never share a cache entry, and every write in
// useAdminUserActions invalidates the ["admin-users"] PREFIX — which matches every
// filter permutation at once.

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { AdminUsersFilters, AdminUsersListData } from "@/hooks/queries/admin-users-types";

export const ADMIN_USERS_QUERY_KEY = "admin-users";

export function adminUsersQueryKey(filters: AdminUsersFilters) {
  return [ADMIN_USERS_QUERY_KEY, filters] as const;
}

/**
 * Builds the prefix-relative request path. Every value is encoded by
 * URLSearchParams; an empty/undefined filter is OMITTED rather than sent blank,
 * so a cleared filter is indistinguishable from one that was never set.
 *
 * `org_id` is threaded here because a super_admin may deep-link to one org's
 * slice (boundary #5). An org_admin's client NEVER sets it — the server scopes
 * that caller from the token, and sending a client-chosen scope would make the
 * refusal in AC10 unreachable.
 */
export function adminUsersPath(filters: AdminUsersFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.role) params.set("role", filters.role);
  if (filters.status) params.set("status", filters.status);
  if (filters.org_id) params.set("org_id", filters.org_id);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));

  const qs = params.toString();
  return qs ? `/admin/users?${qs}` : "/admin/users";
}

export function useAdminUsers(filters: AdminUsersFilters) {
  return useQuery({
    queryKey: adminUsersQueryKey(filters),
    queryFn: () => apiFetch<AdminUsersListData>(adminUsersPath(filters)),
    // A refusal (ADMIN-006/007) and a 404 are both terminal for this read; retrying
    // only delays the inline Banner the screen renders instead.
    retry: false,
  });
}
